// TREATX Legal Admin — Business-First Guided Flows
// ------------------------------------------------
// One file, three flows:
//   • PublishWizard        — Upload DOCX → Preview → New/Replace → Processes → Impact → Publish
//   • RetirementActionsModal — Replace / Remove usages / Cancel
//   • ReconsentConfigModal — Immediate / Grace / Warn / Hard-block
// All reuse the existing backend endpoints — no new engine, no parallel workflow.

import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Loader2, X, Upload, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Search } from 'lucide-react';
import { SafeHtml } from '../../components/security/SafeHtml';

// Kept in sync with LegalSelfServicePages.jsx (same enums, human labels)
const HUMAN_ROLES = [
  { value: 'ANY', label: 'All applicable roles' },
  { value: 'patient', label: 'Patient' },
  { value: 'provider_applicant', label: 'Provider Applicant' },
  { value: 'clinic_admin', label: 'Clinic Admin' },
  { value: 'doctor', label: 'Doctor' },
];
const HUMAN_COUNTRIES = [
  { value: 'ANY', label: 'All countries' },
  { value: 'TR', label: 'Türkiye' },
  { value: 'EU', label: 'EU / EEA' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'ROW', label: 'Rest of World' },
];
const HUMAN_PLATFORMS = [
  { value: 'ANY', label: 'All platforms' }, { value: 'web', label: 'Web' },
  { value: 'ios', label: 'iOS' }, { value: 'android', label: 'Android' },
];
const HUMAN_PSPS = [
  { value: 'ANY', label: 'Any / Not applicable' }, { value: 'stripe', label: 'Stripe' }, { value: 'iyzico', label: 'Iyzico' },
];
const HUMAN_MODES = [
  { value: 'log', label: 'Record only (no user prompt)' },
  { value: 'warn', label: 'Show a warning' },
  { value: 'hard_block', label: 'Block the action until accepted' },
];

// Prefer human title over content_key everywhere.
const humanTitle = (d) => (d?.title?.en) || (d?.title?.tr) || d?.content_key || '(untitled)';

// ──────────────────────────────────────────────────────────────
// PUBLISH WIZARD
// ──────────────────────────────────────────────────────────────
export const PublishWizard = ({ open, onClose, prefill, onPublished }) => {
  // prefill: { mode: 'new'|'replace', content_key?: string } — optional
  const [step, setStep] = useState(1); // 1..6
  const [busy, setBusy] = useState(false);

  // Step 1: Upload DOCX
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {body_html, body_sha256, source_sha256, ...}

  // Step 3: New or Replace
  const [flow, setFlow] = useState(prefill?.mode || 'new'); // 'new' | 'replace'

  // Metadata for a brand-new document
  const [meta, setMeta] = useState({
    title_en: '', doc_code: '', audience: 'all', locale: 'en', internal: false, admin_note: '',
  });

  // Explicit canonical Document Key for brand-new documents (LEGAL-PUBLISH-CANONICAL-KEY-01).
  // Suggested from the title (Turkish-aware), but always reviewed/editable — no random suffix.
  const [newContentKey, setNewContentKey] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);

  // Replacement target
  const [documents, setDocuments] = useState([]);
  const [replaceTarget, setReplaceTarget] = useState(prefill?.content_key || '');

  // Step 4: business processes (multi-select) & scope
  const [journeys, setJourneys] = useState([]);
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [scope, setScope] = useState({ roles: ['ANY'], countries: ['ANY'], platforms: ['ANY'], payment_providers: ['ANY'], mode: 'log' });

  // Step 5: impact
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    if (!open) return;
    setNewContentKey('');
    setKeyEdited(false);
    (async () => {
      try {
        const [a, j] = await Promise.all([
          api.get('/admin/agreements'),
          api.get('/admin/legal/journeys'),
        ]);
        setDocuments((a.data.agreements || []).filter((d) => !d.internal && d.status !== 'archived'));
        setJourneys(j.data.journeys || []);
      } catch (_e) { /* silent */ }
    })();
  }, [open]);

  const renderPreview = async () => {
    if (!file) return toast.error('Choose a .docx file first');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/legal/upload-docx', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'DOCX preview failed');
    } finally { setBusy(false); }
  };

  const computeImpact = async () => {
    setBusy(true);
    try {
      const journey_labels = selectedProcesses.map((k) => journeys.find((j) => j.journey_key === k)?.label || k);
      let historical = 0;
      let reconsent_users = 0;
      let replaced_content_key = null;
      if (flow === 'replace' && replaceTarget) {
        const u = await api.get(`/admin/legal/documents/${replaceTarget}/usage`);
        historical = u.data?.summary?.total_evidence_rows || 0;
        // Reconsent estimate = evidence rows on the current published version
        const curV = documents.find((d) => d.content_key === replaceTarget)?.current_version;
        if (curV) {
          const cur = (u.data?.evidence_row_counts_by_version || []).find((r) => r.content_version === curV);
          reconsent_users = cur?.count || 0;
        }
        replaced_content_key = replaceTarget;
      }
      setImpact({
        journey_labels,
        historical,
        reconsent_users,
        replaced_content_key,
        replaced_title: replaced_content_key ? humanTitle(documents.find((d) => d.content_key === replaced_content_key)) : null,
        bindings_updated: selectedProcesses.length,
      });
      setStep(5);
    } catch (e) {
      toast.error('Impact analysis failed');
    } finally { setBusy(false); }
  };

  const doPublish = async () => {
    setBusy(true);
    try {
      let contentKey = replaceTarget;
      let version = null;

      if (flow === 'new') {
        // Create metadata + draft v1 using the explicitly reviewed Document Key.
        const r = await api.post('/admin/legal/documents', {
          content_key: newContentKey,
          title: meta.title_en,
          doc_code: meta.doc_code || `${(newContentKey || 'doc').slice(0, 12)}-1.0`,
          audience: meta.audience,
          locale: meta.locale,
          internal: !!meta.internal,
          admin_note: meta.admin_note,
          body_html: preview.body_html,
        });
        contentKey = r.data.content_key;
        version = 1;
      } else {
        // Replace flow — upload a new DOCX version onto the existing key
        const fd = new FormData();
        fd.append('file', file);
        fd.append('changelog', `New version via Publish Wizard (${file?.name || ''})`);
        const r = await api.post(`/admin/legal/documents/${replaceTarget}/versions/upload-docx`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        version = r.data.version;
      }

      // Publish the draft
      await api.post(`/admin/agreements/${contentKey}/versions/${version}/publish`);

      // Bind to selected processes through the CANONICAL rule path
      // (TREATX-LEGAL-BINDING-CANON-02). `merge_required_keys` ADDS this
      // document to the existing canonical rule for the exact scope instead
      // of creating a parallel active rule — the root cause of the Public
      // Legal Footer two-rule conflict.
      for (const jk of selectedProcesses) {
        try {
          await api.post('/admin/legal/bindings/upsert-canonical', {
            journey_key: jk,
            required_keys: [contentKey],
            optional_keys: [],
            roles: scope.roles, countries: scope.countries,
            platforms: scope.platforms, payment_providers: scope.payment_providers,
            mode: scope.mode,
            merge_required_keys: true,
          });
        } catch (err) {
          const d = err?.response?.data?.detail;
          toast.error(`Process “${jk}” not updated: ${d?.message || d || 'rule update failed'}`);
        }
      }

      // TREATX-LEGAL-SSOT-CANON-01 — bindings are the canonical journey→
      // document authority consumed directly by the evaluator. The old
      // post-assign `sync-required-for-signup` projection has been retired;
      // no sync call is needed for the binding to take runtime effect.

      toast.success(`Published — ${humanTitle({ title: { en: meta.title_en } }) || contentKey} v${version}`);
      onPublished && onPublished({ content_key: contentKey, version });
      onClose();
    } catch (e) {
      const body = e?.response?.data;
      const d = (body && typeof body === 'object' && body.detail) ? body.detail : body;
      if (d && typeof d === 'object' && d.code === 'DOCUMENT_ALREADY_EXISTS') {
        setKeyEdited(true);
        setStep(3);
        toast.error(`A document with the key “${d.content_key}” already exists. Choose a different Document Key, or use Replace to publish a new version.`);
      } else {
        const msg = (d && typeof d === 'object') ? d.message : d;
        toast.error((typeof msg === 'string' && msg) ? msg : 'Publish failed');
      }
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="publish-wizard">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Publish Wizard — Step {step} of 6</div>
            <div className="text-lg font-semibold text-slate-900">
              {step === 1 && 'Upload the document (DOCX)'}
              {step === 2 && 'Preview the rendered content'}
              {step === 3 && 'Is this a new document or a replacement?'}
              {step === 4 && 'Choose the business processes it applies to'}
              {step === 5 && 'Review the impact before publishing'}
              {step === 6 && 'Publishing…'}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {step === 1 && (
            <>
              <p className="text-slate-600">Upload the Microsoft Word document that will be shown to users. The wizard renders and sanitises the content automatically. The original DOCX and its SHA-256 fingerprint are preserved for audit.</p>
              <div className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center gap-2">
                <Upload className="h-6 w-6 text-slate-400"/>
                <input type="file" accept=".docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="wiz-docx"/>
                {file && <div className="text-xs text-slate-500">{file.name} — {(file.size/1024).toFixed(1)} KB</div>}
              </div>
              <div className="flex justify-between">
                <span/>
                <Button onClick={renderPreview} disabled={!file || busy} data-testid="wiz-render">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <ArrowRight className="h-4 w-4 mr-1"/>} Render preview
                </Button>
              </div>
            </>
          )}

          {step === 2 && preview && (
            <>
              <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4"/> Rendered — integrity <b>{preview.render_integrity_status}</b></div>
              <div className="text-xs text-slate-500">Body SHA-256: {String(preview.body_sha256).slice(0, 16)}… · Source SHA-256: {String(preview.source_sha256).slice(0, 16)}…</div>
              <SafeHtml
                html={preview.body_html}
                className="border rounded p-3 max-h-64 overflow-y-auto prose prose-sm max-w-none"
                testId="wiz-preview-html"
              />
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-1"/> Back</Button>
                <Button onClick={() => setStep(3)} data-testid="wiz-next-3">Continue <ArrowRight className="h-4 w-4 ml-1"/></Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-slate-600">Choose how this document should be treated:</p>
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="wiz-flow-new" onClick={() => setFlow('new')} className={`border rounded-lg p-4 text-left ${flow==='new'?'border-emerald-500 bg-emerald-50':'border-slate-200'}`}>
                  <div className="font-semibold">Brand-new document</div>
                  <div className="text-xs text-slate-600 mt-1">Nothing existed before. Version 1 is created and can be assigned to processes.</div>
                </button>
                <button data-testid="wiz-flow-replace" onClick={() => setFlow('replace')} className={`border rounded-lg p-4 text-left ${flow==='replace'?'border-emerald-500 bg-emerald-50':'border-slate-200'}`}>
                  <div className="font-semibold">Replace an existing document</div>
                  <div className="text-xs text-slate-600 mt-1">Uploads as the next version of an existing document. Previous version is auto-archived after publication; historical consent is preserved.</div>
                </button>
              </div>

              {flow === 'new' && (
                <div className="border rounded p-3 space-y-2">
                  <label className="block"><span className="text-xs uppercase text-slate-500">Document name</span>
                    <input value={meta.title_en} onChange={(e) => { const v = e.target.value; setMeta({ ...meta, title_en: v }); if (!keyEdited) setNewContentKey(_suggestContentKey(v)); }} placeholder="e.g. Patient Privacy Notice" className="w-full mt-1 px-3 py-2 border rounded" data-testid="wiz-new-title"/>
                  </label>
                  <label className="block"><span className="text-xs uppercase text-slate-500">Document Key</span>
                    <input
                      value={newContentKey}
                      onChange={(e) => { setKeyEdited(true); setNewContentKey(e.target.value.toLowerCase().replace(/\s+/g, '_')); }}
                      placeholder="e.g. security_policy"
                      className={`w-full mt-1 px-3 py-2 border rounded font-mono text-sm ${newContentKey && !_isValidContentKey(newContentKey) ? 'border-red-400' : ''}`}
                      data-testid="wiz-new-content-key"/>
                    {newContentKey && !_isValidContentKey(newContentKey) ? (
                      <p className="text-xs text-red-600 mt-1" data-testid="wiz-content-key-error">Use lowercase letters, numbers and underscores only. Must start with a letter (e.g. <code>security_policy</code>).</p>
                    ) : (
                      <p className="text-xs text-slate-500 mt-1">Permanent internal identifier for this document. Lowercase snake_case, starts with a letter. Cannot be changed after publishing.</p>
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label><span className="text-xs uppercase text-slate-500">Audience</span>
                      <select value={meta.audience} onChange={(e) => setMeta({ ...meta, audience: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded" data-testid="wiz-new-audience">
                        <option value="all">All users</option><option value="patient">Patients</option>
                        <option value="provider">Providers</option><option value="clinic_admin">Clinic Admins</option>
                        <option value="multiple">Multiple audiences</option>
                      </select>
                    </label>
                    <label><span className="text-xs uppercase text-slate-500">Language</span>
                      <select value={meta.locale} onChange={(e) => setMeta({ ...meta, locale: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded">
                        <option value="en">English</option><option value="tr">Türkçe</option>
                      </select>
                    </label>
                  </div>
                </div>
              )}

              {flow === 'replace' && (
                <div className="border rounded p-3">
                  <label className="block"><span className="text-xs uppercase text-slate-500">This document replaces</span>
                    <select value={replaceTarget} onChange={(e) => setReplaceTarget(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded" data-testid="wiz-replace-target">
                      <option value="">— Choose an existing document —</option>
                      {documents.map((d) => (
                        <option key={d.content_key} value={d.content_key}>{humanTitle(d)} (currently v{d.current_version || 0})</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-1"/> Back</Button>
                <Button
                  disabled={(flow === 'new' && (!meta.title_en || !_isValidContentKey(newContentKey))) || (flow === 'replace' && !replaceTarget)}
                  onClick={() => setStep(4)} data-testid="wiz-next-4">
                  Continue <ArrowRight className="h-4 w-4 ml-1"/>
                </Button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <p className="text-slate-600">Which business processes will require this document?</p>
              <div className="border rounded max-h-60 overflow-y-auto">
                {journeys.map((j) => (
                  <label key={j.journey_key} className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b">
                    <input type="checkbox" checked={selectedProcesses.includes(j.journey_key)}
                      onChange={() => setSelectedProcesses((p) => p.includes(j.journey_key) ? p.filter((x) => x !== j.journey_key) : [...p, j.journey_key])}
                      data-testid={`wiz-proc-${j.journey_key}`}/>
                    <span>
                      <div className="text-sm font-medium">{j.label} {j.catalog_status !== 'live' && <span className="text-[10px] text-amber-600">({j.catalog_status})</span>}</div>
                      <div className="text-xs text-slate-500">{j.description}</div>
                    </span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <MiniSelect label="Applies to (role)" value={scope.roles} onChange={(v) => setScope({ ...scope, roles: v })} options={HUMAN_ROLES}/>
                <MiniSelect label="Country" value={scope.countries} onChange={(v) => setScope({ ...scope, countries: v })} options={HUMAN_COUNTRIES}/>
                <MiniSelect label="Platform" value={scope.platforms} onChange={(v) => setScope({ ...scope, platforms: v })} options={HUMAN_PLATFORMS}/>
                <MiniSelect label="Payment provider" value={scope.payment_providers} onChange={(v) => setScope({ ...scope, payment_providers: v })} options={HUMAN_PSPS}/>
              </div>
              <label className="block mt-2">
                <span className="text-xs uppercase text-slate-500">What happens if the user has not accepted?</span>
                <select value={scope.mode} onChange={(e) => setScope({ ...scope, mode: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded" data-testid="wiz-mode">
                  {HUMAN_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                </select>
              </label>
              {selectedProcesses.length === 0 && (
                <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="wiz-unbound-info">
                  This document will be published without a process binding. Users will not be asked to accept it as part of a business process.
                </div>
              )}
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="h-4 w-4 mr-1"/> Back</Button>
                <Button onClick={computeImpact} disabled={busy} data-testid="wiz-next-5">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <ArrowRight className="h-4 w-4 mr-1"/>}
                  Review impact
                </Button>
              </div>
            </>
          )}

          {step === 5 && impact && (
            <>
              <div className="border rounded-lg bg-slate-50 p-4 space-y-2">
                <div className="text-xs text-slate-500 uppercase">Business summary</div>
                <div>
                  <b>{flow === 'new' ? 'New document:' : 'Replacing:'}</b>{' '}
                  {flow === 'replace' ? `${impact.replaced_title} — will become v${(documents.find((d) => d.content_key === replaceTarget)?.current_version || 0) + 1}` : meta.title_en}
                </div>
                <div><b>Affected processes:</b>{impact.journey_labels.length === 0 ? (
                  <span data-testid="impact-processes-none"> None</span>
                ) : (
                  <ul className="list-disc list-inside">
                  {impact.journey_labels.map((l, i) => <li key={i}>{l}</li>)}
                </ul>)}</div>
                <div>Historical consent records preserved: <b>{impact.historical.toLocaleString()}</b></div>
                <div>Users requiring re-consent (estimate): <b>{impact.reconsent_users.toLocaleString()}</b></div>
                <div>Legal Packs affected: <b>—</b> (auto)</div>
                <div>Bindings created / updated: <b>{impact.bindings_updated}</b></div>
                <div>Onboarding impact: <b>{impact.bindings_updated === 0 ? 'None' : 'Auto-synchronised'}</b>{impact.bindings_updated === 0 ? '' : ' (no interruption)'}</div>
                <div>Estimated downtime: <b>None</b></div>
              </div>
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(4)}><ArrowLeft className="h-4 w-4 mr-1"/> Back</Button>
                <Button onClick={doPublish} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wiz-publish">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <CheckCircle2 className="h-4 w-4 mr-1"/>} Publish
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Turkish → ASCII transliteration for suggesting clean canonical keys.
const _TR_TRANSLIT = { ç: 'c', ğ: 'g', ı: 'i', İ: 'i', ö: 'o', ş: 's', ü: 'u', Ç: 'c', Ğ: 'g', Ö: 'o', Ş: 's', Ü: 'u' };
const _transliterate = (s) => (s || '').replace(/[çğıİöşüÇĞÖŞÜ]/g, (m) => _TR_TRANSLIT[m] || m);

// Suggest an internal content_key from a human title (Turkish-aware). Lowercase
// snake_case, starts with a letter, ≤120 chars — NO random suffix. Fully editable.
const _suggestContentKey = (title) => {
  const base = _transliterate(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');
  const trimmed = base.slice(0, 120);
  if (!trimmed) return '';
  return (/^[a-z]/.test(trimmed) ? trimmed : `doc_${trimmed}`).slice(0, 120);
};

// Canonical Document Key validation — subset of backend regex ^[a-z][a-z0-9_]*$:
// starts with a lowercase letter, snake_case, no leading/trailing/double underscore.
const _CONTENT_KEY_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
const _isValidContentKey = (k) => !!k && k.length <= 120 && _CONTENT_KEY_RE.test(k);

const MiniSelect = ({ label, value, onChange, options }) => {
  const toggle = (v) => {
    if (v === 'ANY') return onChange(['ANY']);
    const next = value.filter((x) => x !== 'ANY');
    onChange(next.includes(v) ? next.filter((x) => x !== v) : [...next, v]);
  };
  return (
    <div>
      <div className="text-xs uppercase text-slate-500 mb-1">{label}</div>
      <div className="border rounded p-2 flex flex-wrap gap-2 text-xs">
        {options.map((o) => (
          <label key={o.value} className="inline-flex items-center gap-1">
            <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)}/>
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// RETIREMENT ACTIONS MODAL — Part 7
// ──────────────────────────────────────────────────────────────
export const RetirementActionsModal = ({ open, onClose, contentKey, contentTitle, onDone }) => {
  const [check, setCheck] = useState(null);
  const [action, setAction] = useState(''); // '' | 'replace' | 'remove_usages' | 'cancel'
  const [busy, setBusy] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [replacementKey, setReplacementKey] = useState('');

  useEffect(() => {
    if (!open || !contentKey) return;
    (async () => {
      try {
        const [c, a] = await Promise.all([
          api.get(`/admin/legal/documents/${contentKey}/retirement-check`),
          api.get('/admin/agreements'),
        ]);
        setCheck(c.data);
        setDocuments((a.data.agreements || []).filter((d) => !d.internal && d.status !== 'archived' && d.content_key !== contentKey));
      } catch (e) { toast.error('Dependency check failed'); }
    })();
  }, [open, contentKey]);

  const doReplace = async () => {
    if (!replacementKey) return toast.error('Choose a replacement document first');
    setBusy(true);
    try {
      const journey_keys = (check?.blocked_by_bindings || []).map((b) => b.journey_key);
      await api.post(`/admin/legal/documents/${contentKey}/replace`, {
        replacement_content_key: replacementKey,
        move_journeys: [...new Set(journey_keys)],
        inherit_scopes: true,
      });
      toast.success('Rules migrated to the replacement document. Historical consent is preserved.');
      // Now attempt retirement of the old doc — deps have been superseded server-side,
      // so we pass force=true to avoid a race with the just-migrated bindings.
      await api.post(`/admin/agreements/${contentKey}/archive?force=true`);
      toast.success('Old document retired successfully.');
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Replacement failed');
    } finally { setBusy(false); }
  };

  const doRemoveUsages = async () => {
    setBusy(true);
    try {
      for (const b of (check?.blocked_by_bindings || [])) {
        await api.delete(`/admin/legal/policy/bindings/${b.binding_id}`);
      }
      await api.post(`/admin/agreements/${contentKey}/archive?force=true`);
      toast.success('Rules removed and document retired. Historical consent preserved.');
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove usages failed');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  const blockers = check?.blocked_by_bindings || [];
  const packs = check?.blocked_by_packs || [];
  const safe = !!check?.safe_to_retire;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="retirement-modal">
      <div className="bg-white rounded-xl max-w-lg w-full">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="text-lg font-semibold">Retire this document?</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="text-slate-600">
            The document <b>{contentTitle || contentKey}</b> will no longer be used for new actions. Historical acceptance evidence remains preserved.
          </div>

          {!check && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin"/> Checking dependencies…</div>}

          {check && !safe && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3">
              <div className="flex items-center gap-2 text-amber-800 font-medium"><AlertTriangle className="h-4 w-4"/> This document is still required by:</div>
              <ul className="list-disc list-inside mt-1 text-sm">
                {blockers.map((b, i) => (<li key={i}>{b.journey_label}</li>))}
                {packs.map((p, i) => (<li key={`p${i}`}>Legal Pack {p.pack_id}</li>))}
              </ul>
              <div className="mt-3 space-y-2">
                <label className="flex items-center gap-2"><input type="radio" name="action" checked={action==='replace'} onChange={() => setAction('replace')} data-testid="ret-opt-replace"/> Replace with another document</label>
                <label className="flex items-center gap-2"><input type="radio" name="action" checked={action==='remove_usages'} onChange={() => setAction('remove_usages')} data-testid="ret-opt-remove"/> Remove these usages first</label>
                <label className="flex items-center gap-2"><input type="radio" name="action" checked={action==='cancel'} onChange={() => setAction('cancel')} data-testid="ret-opt-cancel"/> Cancel</label>
              </div>
              {action === 'replace' && (
                <div className="mt-2">
                  <select value={replacementKey} onChange={(e) => setReplacementKey(e.target.value)} className="w-full px-3 py-2 border rounded" data-testid="ret-replacement-select">
                    <option value="">— Choose the replacement document —</option>
                    {documents.map((d) => <option key={d.content_key} value={d.content_key}>{humanTitle(d)}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {check && safe && (
            <div className="border border-emerald-300 bg-emerald-50 rounded p-3 text-emerald-800">This document has no active dependencies. Safe to retire.</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            {check && safe && (
              <Button className="bg-red-600 hover:bg-red-700" disabled={busy}
                onClick={async () => { setBusy(true); try { await api.post(`/admin/agreements/${contentKey}/archive`); toast.success('Retired.'); onDone && onDone(); onClose(); } catch (e) { toast.error('Retire failed'); } finally { setBusy(false); } }}
                data-testid="ret-confirm-simple">
                Retire
              </Button>
            )}
            {check && !safe && action === 'replace' && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doReplace} disabled={busy || !replacementKey} data-testid="ret-do-replace">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : null} Replace &amp; Retire
              </Button>
            )}
            {check && !safe && action === 'remove_usages' && (
              <Button className="bg-red-600 hover:bg-red-700" onClick={doRemoveUsages} disabled={busy} data-testid="ret-do-remove">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : null} Remove usages &amp; Retire
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// RECONSENT CONFIG MODAL — Part 10
// ──────────────────────────────────────────────────────────────
export const ReconsentConfigModal = ({ open, onClose, contentKey, contentTitle, onSaved }) => {
  const [policy, setPolicy] = useState({ mode: 'immediate', grace_days: 0, hard_block_after: '' });
  const [busy, setBusy] = useState(false);
  const interceptorActive = false; // Runtime posture — env-gated. Surface honestly.

  useEffect(() => {
    if (!open || !contentKey) return;
    (async () => {
      try {
        const r = await api.get(`/admin/legal/documents/${contentKey}/usage`);
        const cur = (r.data?.reconsent_policies || [])[0];
        if (cur) {
          const mode = cur.hard_block_after ? 'hard_block' : (cur.warn_only ? 'warn' : (cur.grace_days > 0 ? 'grace' : 'immediate'));
          setPolicy({ mode, grace_days: cur.grace_days || 0, hard_block_after: cur.hard_block_after || '' });
        }
      } catch (_e) { /* first-time setup */ }
    })();
  }, [open, contentKey]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { content_key: contentKey, grace_days: 0, warn_only: false, hard_block_after: null };
      if (policy.mode === 'grace') { body.grace_days = Number(policy.grace_days) || 7; }
      if (policy.mode === 'warn') { body.warn_only = true; body.grace_days = Number(policy.grace_days) || 0; }
      if (policy.mode === 'hard_block') { body.hard_block_after = policy.hard_block_after || new Date(Date.now() + 30 * 86400e3).toISOString(); }
      await api.post('/admin/legal/policy/reconsent', body);
      toast.success('Re-consent policy saved');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="reconsent-modal">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="text-lg font-semibold">Configure re-consent</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="text-slate-600">When a new version of <b>{contentTitle || contentKey}</b> is published, decide when existing users must accept it again.</div>
          {!interceptorActive && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3 text-amber-800 text-xs">
              <b>Runtime note:</b> The Re-consent Gate interceptor is currently disabled in this environment.
              Saved policies are recorded but will only affect users once the Re-consent Gate is enabled by engineering.
            </div>
          )}
          <label className="flex items-start gap-2"><input type="radio" name="rc-mode" checked={policy.mode==='immediate'} onChange={() => setPolicy({ ...policy, mode: 'immediate' })} data-testid="rc-immediate"/> <span><b>Immediate</b> — every user must re-accept on their next authenticated request.</span></label>
          <label className="flex items-start gap-2"><input type="radio" name="rc-mode" checked={policy.mode==='grace'} onChange={() => setPolicy({ ...policy, mode: 'grace' })} data-testid="rc-grace"/> <span><b>Grace period</b> — users have a window to re-accept.</span></label>
          {policy.mode === 'grace' && (
            <div className="ml-6"><input type="number" min={1} value={policy.grace_days} onChange={(e) => setPolicy({ ...policy, grace_days: e.target.value })} className="w-24 px-2 py-1 border rounded" data-testid="rc-grace-days"/> <span className="text-xs text-slate-500">day(s)</span></div>
          )}
          <label className="flex items-start gap-2"><input type="radio" name="rc-mode" checked={policy.mode==='warn'} onChange={() => setPolicy({ ...policy, mode: 'warn' })} data-testid="rc-warn"/> <span><b>Warn only</b> — a banner is shown, but users can proceed without re-accepting.</span></label>
          <label className="flex items-start gap-2"><input type="radio" name="rc-mode" checked={policy.mode==='hard_block'} onChange={() => setPolicy({ ...policy, mode: 'hard_block' })} data-testid="rc-hardblock"/> <span><b>Hard block after date</b> — after the effective date, users are blocked until acceptance.</span></label>
          {policy.mode === 'hard_block' && (
            <div className="ml-6"><input type="datetime-local" value={policy.hard_block_after || ''} onChange={(e) => setPolicy({ ...policy, hard_block_after: e.target.value })} className="px-2 py-1 border rounded" data-testid="rc-hardblock-date"/></div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="rc-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : null} Save policy
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default { PublishWizard, RetirementActionsModal, ReconsentConfigModal };
